/**
 * Watrly Input Component
 * ======================
 * مكون حقل الإدخال
 */

import React, { forwardRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Input.css';

const Input = forwardRef(({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  hint,
  icon,
  iconPosition = 'start',
  disabled = false,
  readOnly = false,
  required = false,
  fullWidth = true,
  size = 'md',
  className = '',
  id,
  name,
  autoComplete,
  maxLength,
  min,
  max,
  step,
  ...props
}, ref) => {
  const { isRTL, t } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const containerClasses = [
    'watrly-input',
    `watrly-input--${size}`,
    fullWidth && 'watrly-input--full',
    disabled && 'watrly-input--disabled',
    error && 'watrly-input--error',
    isFocused && 'watrly-input--focused',
    icon && `watrly-input--has-icon-${iconPosition}`,
    className,
  ].filter(Boolean).join(' ');

  const handleFocus = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const togglePassword = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <div className={containerClasses} dir={isRTL ? 'rtl' : 'ltr'}>
      {label && (
        <label htmlFor={inputId} className="watrly-input__label">
          {label}
          {required && <span className="watrly-input__required">*</span>}
        </label>
      )}

      <div className="watrly-input__wrapper">
        {icon && iconPosition === 'start' && (
          <span className="watrly-input__icon watrly-input__icon--start">
            {icon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          name={name}
          type={inputType}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
          className="watrly-input__field"
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          {...props}
        />

        {icon && iconPosition === 'end' && !isPassword && (
          <span className="watrly-input__icon watrly-input__icon--end">
            {icon}
          </span>
        )}

        {isPassword && (
          <button
            type="button"
            className="watrly-input__password-toggle"
            onClick={togglePassword}
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        )}
      </div>

      {error && (
        <p id={`${inputId}-error`} className="watrly-input__error" role="alert">
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={`${inputId}-hint`} className="watrly-input__hint">
          {hint}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
